import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check, FileText, DollarSign, Edit2, ArrowRightCircle, Trash2, X, ChevronDown, ChevronUp, Calendar, User, LogOut, Building } from 'lucide-react';
import { supabase } from '../lib/supabase';

const JobManagementApp = () => {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ 
    title: '', 
    type: '', 
    status: 'pending'
  });
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [selectedTab, setSelectedTab] = useState('active');
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null); // Fixed: moved inside component
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserAndData();
  }, []);

  const fetchUserAndData = async () => {
  try {
    console.log('🔄 fetchUserAndData: Starting...');
    
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('Error getting user:', userError);
      setLoading(false);
      return;
    }
    
    console.log('User found:', user?.id);
    setUser(user);

    if (!user) {
      console.log('No user found, redirecting to auth');
      navigate('/auth');
      setLoading(false);
      return;
    }

    // Try to get company with retry logic
    let retries = 3;
    let companyMembers = null;
    
    while (retries > 0 && !companyMembers) {
      try {
        console.log(`Attempt ${4 - retries}/3 to fetch company...`);
        
        const { data, error: membersError } = await supabase
          .from('company_members')
          .select(`
            company_id,
            companies (*)
          `)
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (membersError) {
          console.error('Error fetching company members:', membersError);
          
          // If it's just a "no rows" error, that's okay
          if (membersError.code !== 'PGRST116') {
            console.warn('Unexpected error:', membersError);
          }
        } else {
          companyMembers = data;
        }
        
        if (!companyMembers) {
          retries--;
          if (retries > 0) {
            // Wait and retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      } catch (err) {
        console.error('Retry error:', err);
        retries--;
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    if (companyMembers?.companies) {
      console.log('Company found:', companyMembers.companies.name);
      setCompany(companyMembers.companies);
      await fetchItems(companyMembers.companies.id);
    } else {
      console.log('No company found for user. User ID:', user.id);
      
      // Check if company_members record exists in database
      console.log('Checking database directly...');
      const { data: directCheck } = await supabase
        .from('company_members')
        .select('*')
        .eq('user_id', user.id);
      
      console.log('Direct database check:', directCheck);
      
      if (!directCheck || directCheck.length === 0) {
        console.error('CRITICAL: User has no company_members record!');
        
        // Show error to user
        setErrorState('Your account is missing company data. Please contact support or sign up again.');
        
        // Optionally sign out the user
        setTimeout(() => {
          supabase.auth.signOut();
          navigate('/auth');
        }, 3000);
      } else {
        console.log('Record exists but query failed:', directCheck);
        setErrorState('Could not load your company data. Please refresh.');
      }
      
      setCompany(null);
    }
  } catch (error) {
    console.error('Error in fetchUserAndData:', error);
    setErrorState('Failed to load your data. Please try again.');
  } finally {
    setLoading(false);
    console.log('✅ fetchUserAndData: Complete');
  }
};

  const fetchItems = async (companyId) => {
    try {
      // First get items
      const { data: itemsData, error: itemsError } = await supabase
        .from('items')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (itemsError) throw itemsError;

      // Then get status history for each item
      const itemsWithHistory = await Promise.all(
        itemsData.map(async (item) => {
          const { data: historyData } = await supabase
            .from('status_history')
            .select('*')
            .eq('item_id', item.id)
            .order('created_at', { ascending: true });

          return {
            ...item,
            createdAt: new Date(item.created_at).toLocaleString(),
            convertedAt: item.converted_at ? new Date(item.converted_at).toLocaleString() : null,
            rejectedAt: item.rejected_at ? new Date(item.rejected_at).toLocaleString() : null,
            statusHistory: historyData || []
          };
        })
      );

      setItems(itemsWithHistory);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const addItem = async () => {
    if (newItem.title && newItem.type && company && user) {
      try {
        const newItemData = {
          company_id: company.id,
          title: newItem.title,
          type: newItem.type,
          status: 'pending',
          created_by: user.id
        };

        const { data: insertedItem, error } = await supabase
          .from('items')
          .insert([newItemData])
          .select()
          .single();

        if (error) throw error;

        // Add initial status history
        await supabase
          .from('status_history')
          .insert([
            {
              item_id: insertedItem.id,
              status: 'pending',
              note: `${newItem.type === 'quote' ? 'Quote' : 'Job'} created`,
              created_by: user.id
            }
          ]);

        // Refresh items
        fetchItems(company.id);
        
        setNewItem({ title: '', type: '', status: 'pending' });
        setIsAddingItem(false);
      } catch (error) {
        console.error('Error adding item:', error);
      }
    }
  };

  const startEditing = (item) => {
    setEditingItem(item);
    setIsAddingItem(false);
  };

  const saveEdit = async () => {
    if (editingItem && company) {
      try {
        const { error } = await supabase
          .from('items')
          .update({ title: editingItem.title })
          .eq('id', editingItem.id);

        if (error) throw error;

        fetchItems(company.id);
        setEditingItem(null);
      } catch (error) {
        console.error('Error updating item:', error);
      }
    }
  };

  const deleteItem = async (id) => {
    if (!company) return;
    
    try {
      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchItems(company.id);
      setShowDeleteConfirm(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const convertToJob = async (quoteId) => {
    if (!company || !user) return;

    try {
      const { error } = await supabase
        .from('items')
        .update({
          type: 'job',
          status: 'pending',
          converted_at: new Date().toISOString(),
          converted_from_quote: true
        })
        .eq('id', quoteId);

      if (error) throw error;

      // Add status history
      await supabase
        .from('status_history')
        .insert([
          {
            item_id: quoteId,
            status: 'pending',
            note: 'Quote converted to job',
            created_by: user.id
          }
        ]);

      fetchItems(company.id);
    } catch (error) {
      console.error('Error converting quote to job:', error);
    }
  };

  const rejectQuote = async (quoteId) => {
    if (!company || !user) return;

    try {
      const { error } = await supabase
        .from('items')
        .update({
          status: 'archived',
          rejected_at: new Date().toISOString(),
          rejection_reason: 'Customer declined'
        })
        .eq('id', quoteId);

      if (error) throw error;

      // Add status history
      await supabase
        .from('status_history')
        .insert([
          {
            item_id: quoteId,
            status: 'archived',
            note: 'Quote rejected by customer',
            created_by: user.id
          }
        ]);

      fetchItems(company.id);
    } catch (error) {
      console.error('Error rejecting quote:', error);
    }
  };

  const updateItemStatus = async (itemId, newStatus) => {
    if (!company || !user) return;

    try {
      const item = items.find(i => i.id === itemId);
      if (!item) return;

      let note = '';
      if (item.type === 'quote' && newStatus === 'completed') {
        note = 'Quote marked as completed';
      } else if (item.type === 'job') {
        if (newStatus === 'completed') note = 'Job marked as completed';
        else if (newStatus === 'invoiced') note = 'Job marked as invoiced';
        else if (newStatus === 'paid') note = 'Job marked as paid';
      }

      const updates = { status: newStatus };
      if (item.type === 'job' && newStatus === 'paid') {
        updates.status = 'archived';
      }

      const { error } = await supabase
        .from('items')
        .update(updates)
        .eq('id', itemId);

      if (error) throw error;

      // Add status history
      await supabase
        .from('status_history')
        .insert([
          {
            item_id: itemId,
            status: updates.status,
            note,
            created_by: user.id
          }
        ]);

      fetchItems(company.id);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      invoiced: 'bg-blue-100 text-blue-800',
      paid: 'bg-purple-100 text-purple-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100';
  };

  const canUpdateStatus = (item) => {
    const jobFlow = {
      pending: 'completed',
      completed: 'invoiced',
      invoiced: 'paid'
    };
    const quoteFlow = {
      pending: 'completed'
    };
    
    return item.type === 'quote' ? quoteFlow[item.status] : jobFlow[item.status];
  };

  const getFilteredItems = () => {
    if (selectedTab === 'quotes') {
      return items.filter(item => 
        item.type === 'quote' && 
        (item.status === 'pending' || item.status === 'completed')
      );
    }
    if (selectedTab === 'archived') {
      return items.filter(item => item.status === 'archived');
    }
    return items.filter(item => item.type === 'job' && item.status !== 'archived');
  };

  const toggleMonthExpand = (monthYear) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthYear]: !prev[monthYear]
    }));
  };

  const getArchivedItemsByMonth = () => {
    const archivedItems = items.filter(item => item.status === 'archived');
    const itemsByMonth = {};
    
    archivedItems.forEach(item => {
      const date = new Date(item.created_at);
      const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
      
      if (!itemsByMonth[monthYear]) {
        itemsByMonth[monthYear] = [];
      }
      itemsByMonth[monthYear].push(item);
    });
    
    const sortedMonths = {};
    Object.keys(itemsByMonth)
      .sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB - dateA;
      })
      .forEach(month => {
        sortedMonths[month] = itemsByMonth[month];
      });
    
    return sortedMonths;
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const filteredItems = getFilteredItems();
  const archivedByMonth = getArchivedItemsByMonth();

 if (loading) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600 mb-2">Loading your dashboard...</p>
      <p className="text-sm text-gray-500">This may take a moment</p>
      {errorState && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg max-w-md">
          <p className="text-red-700">{errorState}</p>
        </div>
      )}
    </div>
  );
}

if (errorState) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
        <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading App</h2>
        <p className="text-red-600 mb-4">{errorState}</p>
        <div className="space-y-2">
          <button
            onClick={() => {
              setErrorState(null);
              fetchUserAndData();
            }}
            className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700"
          >
            Try Again
          </button>
          <button
            onClick={() => {
              supabase.auth.signOut();
              navigate('/auth');
            }}
            className="w-full bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

  if (!user || !company) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="max-w-md mx-auto p-4 bg-gray-50 min-h-screen">
      {/* Header with User Info */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Management App</h1>
          <div className="flex items-center gap-2 mt-1">
            <Building size={16} className="text-gray-500" />
            <span className="text-sm text-gray-600">{company.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
            <User size={20} className="text-gray-600" />
            <span className="text-sm font-medium">{user.email}</span>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            title="Logout"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 bg-white rounded-lg p-1 shadow">
        <button
          onClick={() => setSelectedTab('active')}
          className={`flex-1 py-2 px-4 rounded ${
            selectedTab === 'active' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setSelectedTab('quotes')}
          className={`flex-1 py-2 px-4 rounded ${
            selectedTab === 'quotes' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Quotes
        </button>
        <button
          onClick={() => setSelectedTab('archived')}
          className={`flex-1 py-2 px-4 rounded ${
            selectedTab === 'archived' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Archived
        </button>
      </div>

      {/* Add New Form */}
      {isAddingItem && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <textarea
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 min-h-24"
              placeholder="Enter title"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setNewItem({ ...newItem, type: 'quote' })}
                className={`flex-1 p-2 rounded ${
                  newItem.type === 'quote' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                Quote
              </button>
              <button
                onClick={() => setNewItem({ ...newItem, type: 'job' })}
                className={`flex-1 p-2 rounded ${
                  newItem.type === 'job' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                Job
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={addItem}
              disabled={!newItem.type}
              className={`flex-1 p-2 rounded ${
                newItem.type
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Add {newItem.type || 'Item'}
            </button>
            <button
              onClick={() => setIsAddingItem(false)}
              className="flex-1 bg-gray-200 text-gray-800 p-2 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Item Button */}
      {!isAddingItem && !editingItem && (
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setIsAddingItem(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-lg"
          >
            <Plus size={20} />
            Add New {selectedTab === 'quotes' ? 'Quote' : selectedTab === 'active' ? 'Job' : 'Item'}
          </button>
        </div>
      )}

      {/* Item List */}
      {selectedTab !== 'archived' && (
        <div className="space-y-4">
          {filteredItems.map(item => (
            <div key={item.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-500 capitalize">{item.type}</span>
                    <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <h3 className="font-semibold text-lg whitespace-pre-line">{item.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">Created: {item.createdAt}</p>
                  
                  {/* Status History */}
                  {item.statusHistory && item.statusHistory.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {item.statusHistory.map((history, index) => (
                        <div key={history.id || index} className="text-xs text-gray-600">
                          <span className="font-medium">{history.note}</span>
                          <span className="ml-2">
                            {new Date(history.created_at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {item.convertedAt && (
                    <p className="text-sm text-blue-500 mt-1">Converted to job: {item.convertedAt}</p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                {/* Edit button for quotes and jobs */}
                {(item.type === 'quote' && item.status !== 'archived') || (item.type === 'job' && item.status !== 'archived') ? (
                  <button
                    onClick={() => startEditing(item)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1"
                  >
                    <Edit2 size={18} />
                    Edit
                  </button>
                ) : null}
                
                {/* Quote-specific actions */}
                {selectedTab === 'quotes' && item.type === 'quote' && (
                  <>
                    {item.status === 'pending' ? (
                      <button
                        onClick={() => updateItemStatus(item.id, 'completed')}
                        className="flex-1 p-2 bg-blue-600 text-white rounded flex items-center justify-center gap-2 hover:bg-blue-700"
                      >
                        <Check size={18} />
                        Mark as Completed
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => convertToJob(item.id)}
                          className="flex-1 p-2 bg-green-600 text-white rounded flex items-center justify-center gap-2 hover:bg-green-700"
                        >
                          <ArrowRightCircle size={18} />
                          Convert to Job
                        </button>
                        <button
                          onClick={() => rejectQuote(item.id)}
                          className="flex-1 p-2 bg-red-600 text-white rounded flex items-center justify-center gap-2 hover:bg-red-700"
                        >
                          <X size={18} />
                          Quote Rejected
                        </button>
                      </>
                    )}
                  </>
                )}
                
                {/* Job-specific actions */}
                {selectedTab === 'active' && item.type === 'job' && canUpdateStatus(item) && (
                  <button
                    onClick={() => updateItemStatus(item.id, canUpdateStatus(item))}
                    className="flex-1 p-2 bg-blue-600 text-white rounded flex items-center justify-center gap-2 hover:bg-blue-700"
                  >
                    {item.status === 'pending' && <Check size={18} />}
                    {item.status === 'completed' && <FileText size={18} />}
                    {item.status === 'invoiced' && <DollarSign size={18} />}
                    Mark as {canUpdateStatus(item)}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archived Items - Organized by Month */}
      {selectedTab === 'archived' && (
        <div className="space-y-4">
          {Object.keys(archivedByMonth).length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <p>No archived items yet.</p>
            </div>
          ) : (
            Object.entries(archivedByMonth).map(([monthYear, monthItems]) => (
              <div key={monthYear} className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleMonthExpand(monthYear)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-gray-500" />
                    <span className="font-semibold text-gray-800">{monthYear}</span>
                    <span className="text-sm text-gray-500">({monthItems.length} items)</span>
                  </div>
                  {expandedMonths[monthYear] ? (
                    <ChevronUp size={20} className="text-gray-500" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-500" />
                  )}
                </button>
                
                {expandedMonths[monthYear] && (
                  <div className="px-4 pb-4 space-y-3">
                    {monthItems.map(item => (
                      <div key={item.id} className="border-l-4 border-gray-300 pl-3 py-2">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm text-gray-500 capitalize">{item.type}</span>
                              {item.rejection_reason && (
                                <span className="text-sm text-red-600">Rejected</span>
                              )}
                            </div>
                            <h4 className="font-medium text-gray-800 whitespace-pre-line">{item.title}</h4>
                            <p className="text-xs text-gray-500 mt-1">Created: {item.createdAt}</p>
                            
                            {/* Status History */}
                            {item.statusHistory && item.statusHistory.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {item.statusHistory.map((history, index) => (
                                  <div key={history.id || index} className="text-xs text-gray-500">
                                    <span className="font-medium">{history.note}</span>
                                    <span className="ml-1">
                                      {new Date(history.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {item.convertedAt && (
                              <p className="text-xs text-blue-500 mt-1">Converted: {item.convertedAt}</p>
                            )}
                            {item.rejectedAt && (
                              <p className="text-xs text-red-500 mt-1">Rejected: {item.rejectedAt}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-2">
                          <button
                            onClick={() => startEditing(item)}
                            className="text-sm p-1 text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1"
                          >
                            <Edit2 size={14} />
                            Edit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Empty State for Active and Quotes tabs */}
      {filteredItems.length === 0 && !isAddingItem && !editingItem && selectedTab !== 'archived' && (
        <div className="text-center text-gray-500 mt-8">
          <p>No {selectedTab} items yet. {selectedTab === 'active' && 'Click the "Add New Job" button to add a new job.'}</p>
        </div>
      )}

      {/* Full Screen Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
          <div className="bg-white shadow px-4 py-3 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Edit {editingItem.type}</h2>
            <button
              onClick={() => setEditingItem(null)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <textarea
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-32"
                  placeholder="Enter title"
                />
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500">Created: {editingItem.createdAt}</p>
                
                {/* Status History in Edit Modal */}
                {editingItem.statusHistory && editingItem.statusHistory.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-700 mb-2">Status History</h4>
                    <div className="space-y-2">
                      {editingItem.statusHistory.map((history, index) => (
                        <div key={history.id || index} className="flex items-start text-sm">
                          <div className={`w-3 h-3 rounded-full mt-1 mr-2 ${
                            history.status === 'pending' ? 'bg-yellow-500' :
                            history.status === 'completed' ? 'bg-green-500' :
                            history.status === 'invoiced' ? 'bg-blue-500' :
                            history.status === 'paid' ? 'bg-purple-500' :
                            history.status === 'archived' ? 'bg-gray-500' : 'bg-gray-300'
                          }`}></div>
                          <div>
                            <div className="font-medium">{history.note}</div>
                            <div className="text-gray-500 text-xs">
                              {new Date(history.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {editingItem.convertedAt && (
                  <p className="text-sm text-blue-500 mt-1">Converted to job: {editingItem.convertedAt}</p>
                )}
                {editingItem.rejectedAt && (
                  <p className="text-sm text-red-500 mt-1">Rejected: {editingItem.rejectedAt}</p>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  className="flex-1 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(editingItem.id)}
                  className="p-3 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
                >
                  <Trash2 size={20} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this item? This action cannot be undone.</p>
            <div className="flex gap-2">
              <button
                onClick={() => deleteItem(showDeleteConfirm)}
                className="flex-1 bg-red-600 text-white p-2 rounded hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 bg-gray-200 text-gray-800 p-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagementApp;